'use client';

import { useCallback, useState } from 'react';
import { useOptimizeRouteMutation } from 'src/api/redux';
import type { RouteStopResponse } from 'src/api/redux/routes';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import {
  selectRouteNavigationHydrated,
  setCurrentStop,
  setRoundTrip,
} from 'src/redux/slices/routeNavigationSlice';
import useUserLocation from 'src/hooks/geolocation/useUserLocation';

export const OPTIMIZE_LOCATION_ERROR =
  'Allow location access to optimize from your current position.';
export const OPTIMIZE_FAILED_ERROR = "Couldn't optimize this route.";

export interface UseRouteOptimizeReturn {
  /**
   * Reorder a saved route's stops from the user's current location, or from
   * `origin` when the caller already has it. Resolves the optimized stops; on
   * failure, or before the saved progress hydrates, resolves null and sets
   * `error` where there is something to tell the user.
   */
  optimize: (params: {
    routeId: number | string;
    roundtrip: boolean;
    origin?: { lat: number; lng: number };
  }) => Promise<RouteStopResponse[] | null>;
  isOptimizing: boolean;
  error: string | null;
  /** Lets the caller clear the error, or report a failure of its own. */
  setError: (message: string | null) => void;
}

/**
 * Optimizes a saved route through the backend (`POST v1/routes/{id}/optimize/`),
 * which persists the origin on the route and reorders stops in place. Asks for
 * browser location when it isn't available yet. A successful optimize re-plans
 * the route, so the active stop is cleared and the round-trip choice is kept.
 */
const useRouteOptimize = (): UseRouteOptimizeReturn => {
  const dispatch = useAppDispatch();
  const isNavigationHydrated = useAppSelector(selectRouteNavigationHydrated);
  const { location, requestLocation } = useUserLocation();
  const [optimizeRoute, { isLoading: isOptimizing }] =
    useOptimizeRouteMutation();
  const [error, setError] = useState<string | null>(null);

  const optimize = useCallback(
    async ({
      routeId,
      roundtrip,
      origin: knownOrigin,
    }: {
      routeId: number | string;
      roundtrip: boolean;
      origin?: { lat: number; lng: number };
    }) => {
      // The server saves the new order and the client records the round trip
      // choice and clears the active stop. Hydration would overwrite those
      // client writes, so don't start until it has finished.
      if (!isNavigationHydrated) return null;

      setError(null);

      const origin = knownOrigin ?? location ?? (await requestLocation());
      if (!origin) {
        setError(OPTIMIZE_LOCATION_ERROR);
        return null;
      }

      try {
        const { stops } = await optimizeRoute({
          routeId,
          origin: { lat: origin.lat, lng: origin.lng },
          roundtrip,
        }).unwrap();
        if (!stops) {
          setError(OPTIMIZE_FAILED_ERROR);
          return null;
        }
        dispatch(setCurrentStop({ routeId: Number(routeId), stopKey: null }));
        dispatch(setRoundTrip({ routeId: Number(routeId), roundTrip: roundtrip }));
        return stops;
      } catch {
        // 4xx/5xx are already reported by the API layer
        setError(OPTIMIZE_FAILED_ERROR);
        return null;
      }
    },
    [location, requestLocation, optimizeRoute, isNavigationHydrated, dispatch]
  );

  return { optimize, isOptimizing, error, setError };
};

export default useRouteOptimize;
